import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/e148fb924158909d7a396adc24b7b235/31f2a1bf7ffa03abe878d7016acdab15.jpeg" 
                alt="ODUK & COMPANY ADVOCATES Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <div className={`font-bold text-lg ${isScrolled ? 'text-gray-900' : 'text-white'}`}>ODUK & COMPANY</div>
                <div className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-gray-200'}`}>ADVOCATES</div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#d4af37]' : 'text-white hover:text-[#d4af37]'}`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#d4af37]' : 'text-white hover:text-[#d4af37]'}`}>About</button>
              <button onClick={() => scrollToSection('practice-areas')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#d4af37]' : 'text-white hover:text-[#d4af37]'}`}>Practice Areas</button>
              <button onClick={() => scrollToSection('values')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#d4af37]' : 'text-white hover:text-[#d4af37]'}`}>Values</button>
              <button onClick={() => scrollToSection('team')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#d4af37]' : 'text-white hover:text-[#d4af37]'}`}>Team</button>
              <button onClick={() => scrollToSection('contact')} className="bg-[#d4af37] text-gray-900 px-6 py-2 rounded-full text-sm font-semibold hover:bg-[#c49d2e] transition-colors whitespace-nowrap cursor-pointer">Contact Us</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">About</button>
              <button onClick={() => scrollToSection('practice-areas')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">Practice Areas</button>
              <button onClick={() => scrollToSection('values')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">Values</button>
              <button onClick={() => scrollToSection('team')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">Team</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 cursor-pointer">Contact</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-[70vh] min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/e148fb924158909d7a396adc24b7b235/eafbf7f00dcfebc67159eaa40495916b.jpeg" 
            alt="Law Office" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <div className="text-[#d4af37] text-lg font-semibold mb-4">Est. 1988</div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              ODUK & COMPANY<br />ADVOCATES
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Trusted Legal Excellence Since 1988. Kenya's leading law firm delivering transformative and client-centered legal solutions.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => scrollToSection('contact')} className="bg-[#d4af37] text-gray-900 px-8 py-4 rounded-full text-base font-semibold hover:bg-[#c49d2e] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer">
                Schedule Consultation
              </button>
              <button onClick={() => scrollToSection('practice-areas')} className="bg-transparent border-2 border-[#d4af37] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#d4af37] hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                Our Practice Areas <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-[#faf8f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="inline-block bg-[#d4af37] text-gray-900 px-4 py-1 rounded-full text-sm font-semibold mb-8">
            Since 1988
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2">
              <h2 className="text-4xl md:text-5xl font-bold text-[#1a2332] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                About ODUK & COMPANY
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ODUK & COMPANY ADVOCATES is a leading and highly respected law firm in Kenya, having been in continuous legal practice since 1988. With over three decades of excellence, we have built a reputation for delivering exceptional legal services across diverse practice areas.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our firm combines traditional legal expertise with modern innovative strategies, ensuring that our clients receive comprehensive, timely, and effective legal solutions. We pride ourselves on our commitment to justice, integrity, and client satisfaction.
              </p>
            </div>

            <div className="flex flex-col justify-center">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#d4af37] mt-2"></div>
                  <div>
                    <div className="text-[#d4af37] font-bold text-lg">1988</div>
                    <div className="text-gray-600 text-sm">Firm Established</div>
                  </div>
                </div>
                <div className="border-l-2 border-[#d4af37] h-12 ml-1"></div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#d4af37] mt-2"></div>
                  <div>
                    <div className="text-[#d4af37] font-bold text-lg">35+ Years</div>
                    <div className="text-gray-600 text-sm">Legal Excellence</div>
                  </div>
                </div>
                <div className="border-l-2 border-[#d4af37] h-12 ml-1"></div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#d4af37] mt-2"></div>
                  <div>
                    <div className="text-[#d4af37] font-bold text-lg">2025</div>
                    <div className="text-gray-600 text-sm">Continued Growth</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-[#d4af37] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-full mb-4">
                <i className="ri-eye-line text-2xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-3">Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted and transformative law firm through providing plug-in and impact driven advocacy.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-[#d4af37] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-full mb-4">
                <i className="ri-compass-line text-2xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-3">Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To timeously deliver client centered legal solutions under one roof upholding justice and the rule of law.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-[#d4af37] hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-full mb-4">
                <i className="ri-award-line text-2xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-3">Heritage</h3>
              <p className="text-gray-600 leading-relaxed">
                Over 35 years of continuous legal practice, serving individuals, corporations, and communities across Kenya.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section id="values" className="py-20 bg-[#1a2332]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Core Values
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Integrity</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We uphold honesty, transparency, and ethical practice in all our engagements.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-briefcase-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Professionalism</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We strive for the highest standards of professionalism and quality in every legal service we provide.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Client-Centricity</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Our clients' needs drive every decision we make and every case we handle.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-lightbulb-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Innovation</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We embrace modern legal strategies and technology to deliver effective and efficient solutions.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Teamwork</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We believe in collaboration, respect, and shared success within our firm and with our clients.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 hover:shadow-2xl transition-all hover:border-2 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center bg-[#d4af37] rounded-full mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-lock-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] text-center mb-4">Confidentiality</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We safeguard our clients' information with the highest level of discretion and professionalism.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Section */}
      <section id="practice-areas" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a2332] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Practice Areas
            </h2>
            <div className="w-16 h-1 bg-[#d4af37]"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Featured Card - Litigation */}
            <div className="md:col-span-2 lg:col-span-1 bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-scales-3-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Litigation</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Civil Litigation</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Criminal Litigation</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Alternative Dispute Resolution */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-discuss-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Alternative Dispute Resolution</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Mediation</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Arbitration</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Commercial & Corporate Law */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-building-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Commercial & Corporate Law</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Corporate advisory services</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Commercial transactions</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Business legal compliance</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Employment & Industrial Relations */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-group-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Employment & Industrial Relations</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Employment disputes</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Labour relations</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Trade unions advisory</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Planning & Environment */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-plant-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Planning, Environment & Local Government</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Environmental compliance</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Land use and planning</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Constitutional & Human Rights */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-government-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Constitutional & Human Rights</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Constitutional petitions</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Protection of fundamental rights</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Conveyancing & Property */}
            <div className="md:col-span-2 lg:col-span-1 bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-home-4-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Conveyancing & Property Law</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Legal drafting (leases, affidavits)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Registration (charges, leases)</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Real estate transactions</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Property transfers</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Insurance Law */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-shield-user-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Insurance Law</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Insurance claims advisory</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Dispute resolution</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Family Law & Succession */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-parent-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Family Law & Succession</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Family disputes</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Probate and administration</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Succession planning</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>

            {/* Pro Bono */}
            <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all hover:border-l-8 hover:border-[#d4af37] group cursor-pointer">
              <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4">
                <i className="ri-hand-heart-line text-3xl text-[#d4af37]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1a2332] mb-4">Pro Bono Legal Services</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Community legal assistance</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-[#d4af37] mt-1">•</span>
                  <span>Access to justice initiatives</span>
                </li>
              </ul>
              <div className="text-[#d4af37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                Learn More <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 bg-[#faf8f3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1a2332]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Meet Our Team
              </h2>
            </div>
            <div className="flex items-center">
              <p className="text-lg text-gray-700 leading-relaxed">
                Our diverse team of legal professionals brings together decades of combined experience, working collaboratively to deliver exceptional results for our clients.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Advocates */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-80 bg-[#1a2332] flex flex-col items-center justify-between p-6 hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 right-4 w-24 h-24 rounded-full border-4 border-[#d4af37]"></div>
                <div className="absolute bottom-16 left-4 w-16 h-16 rounded-full border-4 border-[#d4af37]"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border-2 border-[#d4af37]"></div>
              </div>
              <div className="relative flex-1 flex flex-col items-center justify-center w-full">
                <div className="w-24 h-24 rounded-full bg-[#d4af37] flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-scales-3-line text-4xl text-white"></i>
                </div>
                <div className="bg-[#d4af37]/20 border border-[#d4af37]/40 rounded-full px-3 py-1 mb-2">
                  <span className="text-[#d4af37] text-xs font-semibold tracking-wider uppercase">Legal Team</span>
                </div>
              </div>
              <div className="relative w-full">
                <h3 className="text-xl font-bold text-white mb-1">Advocates</h3>
                <p className="text-sm text-gray-400">Experienced courtroom & advisory professionals</p>
              </div>
            </div>

            {/* Paralegals */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-80 bg-[#2a3442] flex flex-col items-center justify-between p-6 hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 right-4 w-24 h-24 rounded-full border-4 border-[#d4af37]"></div>
                <div className="absolute bottom-16 left-4 w-16 h-16 rounded-full border-4 border-[#d4af37]"></div>
              </div>
              <div className="relative flex-1 flex flex-col items-center justify-center w-full">
                <div className="w-24 h-24 rounded-full bg-[#d4af37] flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-file-text-line text-4xl text-white"></i>
                </div>
                <div className="bg-[#d4af37]/20 border border-[#d4af37]/40 rounded-full px-3 py-1 mb-2">
                  <span className="text-[#d4af37] text-xs font-semibold tracking-wider uppercase">Support Team</span>
                </div>
              </div>
              <div className="relative w-full">
                <h3 className="text-xl font-bold text-white mb-1">Paralegals</h3>
                <p className="text-sm text-gray-400">Skilled legal research & documentation experts</p>
              </div>
            </div>

            {/* Interns */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-80 bg-[#1a2332] flex flex-col items-center justify-between p-6 hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 right-4 w-24 h-24 rounded-full border-4 border-[#d4af37]"></div>
                <div className="absolute bottom-16 left-4 w-16 h-16 rounded-full border-4 border-[#d4af37]"></div>
              </div>
              <div className="relative flex-1 flex flex-col items-center justify-center w-full">
                <div className="w-24 h-24 rounded-full bg-[#d4af37] flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-graduation-cap-line text-4xl text-white"></i>
                </div>
                <div className="bg-[#d4af37]/20 border border-[#d4af37]/40 rounded-full px-3 py-1 mb-2">
                  <span className="text-[#d4af37] text-xs font-semibold tracking-wider uppercase">Rising Stars</span>
                </div>
              </div>
              <div className="relative w-full">
                <h3 className="text-xl font-bold text-white mb-1">Interns</h3>
                <p className="text-sm text-gray-400">Future legal professionals in training</p>
              </div>
            </div>

            {/* Office Administrators */}
            <div className="relative rounded-2xl overflow-hidden group cursor-pointer h-80 bg-[#2a3442] flex flex-col items-center justify-between p-6 hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-4 right-4 w-24 h-24 rounded-full border-4 border-[#d4af37]"></div>
                <div className="absolute bottom-16 left-4 w-16 h-16 rounded-full border-4 border-[#d4af37]"></div>
              </div>
              <div className="relative flex-1 flex flex-col items-center justify-center w-full">
                <div className="w-24 h-24 rounded-full bg-[#d4af37] flex items-center justify-center mb-4 shadow-lg">
                  <i className="ri-customer-service-2-line text-4xl text-white"></i>
                </div>
                <div className="bg-[#d4af37]/20 border border-[#d4af37]/40 rounded-full px-3 py-1 mb-2">
                  <span className="text-[#d4af37] text-xs font-semibold tracking-wider uppercase">Operations</span>
                </div>
              </div>
              <div className="relative w-full">
                <h3 className="text-xl font-bold text-white mb-1">Office Administrators</h3>
                <p className="text-sm text-gray-400">Essential backbone of our daily operations</p>
              </div>
            </div>
          </div>

          {/* Team CTA */}
          <div className="mt-12 text-center bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <p className="text-gray-600 text-lg mb-4">
              Our team is united by a shared commitment to <strong className="text-[#1a2332]">justice, excellence, and client success</strong>.
            </p>
            <button onClick={() => scrollToSection('contact')} className="bg-[#d4af37] text-gray-900 px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#c49d2e] transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
              Work With Our Team <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1a2332] to-[#2a3442] rounded-[32px] overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left Side - Contact Info */}
              <div className="p-12 lg:p-16">
                <div className="inline-block bg-[#d4af37] text-gray-900 px-4 py-1 rounded-full text-sm font-semibold mb-6">
                  Get In Touch
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ready to Discuss Your Legal Needs?
                </h2>
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  Contact us today for a consultation. Our team is ready to provide you with expert legal guidance and support.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Call Us</div>
                      <a href="tel:+254705155730" className="text-white font-semibold hover:text-[#d4af37] transition-colors cursor-pointer">+254 705 155 730</a>
                      <br />
                      <a href="tel:+254727980760" className="text-white font-semibold hover:text-[#d4af37] transition-colors cursor-pointer">+254 727 980 760</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-whatsapp-line text-xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">WhatsApp</div>
                      <a href="https://wa.me/254705155730" target="_blank" rel="noopener noreferrer" className="text-white font-semibold hover:text-[#d4af37] transition-colors cursor-pointer">Chat on WhatsApp</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Email</div>
                      <a href="mailto:odukeze@gmail.com" className="text-white font-semibold hover:text-[#d4af37] transition-colors cursor-pointer">odukeze@gmail.com</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#d4af37]/10 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Visit Us</div>
                      <div className="text-white font-semibold">
                        Nairobi Town House, 5th Floor<br />
                        Kaunda Street, Nairobi
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-700">
                  <div className="text-gray-400 text-sm mb-3">Office Hours</div>
                  <div className="text-white">Monday - Friday: 8:00 AM - 5:00 PM</div>
                  <div className="text-white">Saturday: 9:00 AM - 1:00 PM</div>
                </div>

                <div className="mt-8 rounded-2xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.8176!2d36.8219!3d-1.2833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d664b3e3b3%3A0x4e4e4e4e4e4e4e4e!2sNairobi%20Town%20House%2C%20Kaunda%20St%2C%20Nairobi!5e0!3m2!1sen!2ske!4v1700000000000!5m2!1sen!2ske"
                    width="100%"
                    height="220"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="ODUK & COMPANY ADVOCATES Location"
                    className="w-full"
                  ></iframe>
                </div>
              </div>

              {/* Right Side - Contact Form */}
              <div className="bg-white p-12 lg:p-16">
                <h3 className="text-2xl font-bold text-[#1a2332] mb-6">Send Us a Message</h3>
                <form id="contact-form" data-readdy-form className="space-y-6" onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const formData = new FormData(form);
                  
                  // Validate required fields
                  const name = formData.get('name') as string;
                  const email = formData.get('email') as string;
                  const phone = formData.get('phone') as string;
                  const message = formData.get('message') as string;
                  
                  if (!name || !email || !phone || !message) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  
                  // Validate message length
                  if (message.length > 500) {
                    alert('Message must be 500 characters or less');
                    return;
                  }
                  
                  // Submit form
                  fetch('https://readdy.ai/api/form/d6oj42jlgirdouph2rsg', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  })
                    .then(response => {
                      if (response.ok) {
                        alert('Thank you for contacting us! We will get back to you soon.');
                        form.reset();
                      } else {
                        alert('There was an error submitting your message. Please try again.');
                      }
                    })
                    .catch(() => {
                      alert('There was an error submitting your message. Please try again.');
                    });
                }}>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                      placeholder="+254 700 000 000"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                    <input 
                      type="text" 
                      id="subject" 
                      name="subject" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                      placeholder="Legal consultation"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message * (Max 500 characters)</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      required 
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent resize-none"
                      placeholder="Tell us about your legal needs..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="w-full bg-[#d4af37] text-gray-900 px-8 py-4 rounded-full text-base font-semibold hover:bg-[#c49d2e] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white pt-16 pb-8 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Left Column */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/e148fb924158909d7a396adc24b7b235/31f2a1bf7ffa03abe878d7016acdab15.jpeg" 
                  alt="ODUK & COMPANY ADVOCATES Logo" 
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <div className="font-bold text-xl" style={{ fontFamily: 'Playfair Display, serif' }}>ODUK & COMPANY</div>
                  <div className="text-[#d4af37] text-sm">Est. 1988</div>
                </div>
              </div>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                Kenya's trusted law firm delivering transformative legal solutions with integrity and excellence.
              </p>
            </div>

            {/* Middle Column */}
            <div>
              <h4 className="text-[#d4af37] text-sm font-bold uppercase mb-4 tracking-wider">Quick Links</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">About</button>
                <button onClick={() => scrollToSection('practice-areas')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">Practice Areas</button>
                <button onClick={() => scrollToSection('values')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">Values</button>
                <button onClick={() => scrollToSection('team')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">Team</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-white transition-colors hover:underline cursor-pointer">Contact</button>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <h4 className="text-[#d4af37] text-sm font-bold uppercase mb-4 tracking-wider">Contact Us</h4>
              <div className="space-y-3 text-gray-400 text-sm">
                <div className="flex items-start gap-2">
                  <i className="ri-phone-line text-[#d4af37] mt-1"></i>
                  <div>
                    <a href="tel:+254705155730" className="hover:text-white transition-colors cursor-pointer">+254 705 155 730</a><br />
                    <a href="tel:+254727980760" className="hover:text-white transition-colors cursor-pointer">+254 727 980 760</a>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-whatsapp-line text-[#d4af37] mt-1"></i>
                  <a href="https://wa.me/254705155730" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">WhatsApp</a>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-mail-line text-[#d4af37] mt-1"></i>
                  <a href="mailto:odukeze@gmail.com" className="hover:text-white transition-colors cursor-pointer">odukeze@gmail.com</a>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-[#d4af37] mt-1"></i>
                  <div>
                    Nairobi Town House, 5th Floor<br />
                    Kaunda Street, Nairobi
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm">
                © 2025 ODUK & COMPANY ADVOCATES. All Rights Reserved.
              </div>
              <div className="flex items-center gap-6">
                <a href="#" className="text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-[#d4af37] transition-colors cursor-pointer">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
